import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Vertical_Navigation_Bar = () => {
  const navItems = ["Home", "About", "Services", "Portfolio", "Contact"];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📑 Vertical Navigation Bars</h1>

      <p className="tutorial-intro">
        Vertical navigation bars are often used for <b>side menus</b> or{" "}
        <b>dashboards</b>. Below are 10 different examples with unique styles.
      </p>

      {/* List of Topics */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          📘 Types of Vertical Navigation Bars
        </h2>
        <ul style={{ lineHeight: "1.8", marginLeft: "1.2rem" }}>
          <li>1️⃣ Basic gray sidebar</li>
          <li>2️⃣ Dark theme</li>
          <li>3️⃣ Gradient background</li>
          <li>4️⃣ Bordered menu</li>
          <li>5️⃣ Rounded buttons</li>
          <li>6️⃣ With icons</li>
          <li>7️⃣ Active link highlight</li>
          <li>8️⃣ Minimal outline</li>
          <li>9️⃣ Neon glow effect</li>
          <li>🔟 Card-style menu</li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic Vertical Navbar</h2>
        <CodeCopyBlock
          code={`ul.basic-vertical li a:hover {
  background: #d1d5db;
  transition: 0.3s;
}`}
        />
        <ul
          className="basic-vertical"
          style={{
            listStyle: "none",
            padding: 0,
            width: "200px",
            background: "#f3f4f6",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  display: "block",
                  padding: "12px",
                  textDecoration: "none",
                  color: "#111827",
                  transition: "0.3s",
                }}
                onMouseOver={(e) => (e.target.style.background = "#d1d5db")}
                onMouseOut={(e) => (e.target.style.background = "transparent")}
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Dark Sidebar</h2>
        <CodeCopyBlock
          code={`ul.dark-vertical li a:hover {
  background: #2563eb;
  transform: translateX(5px);
  transition: 0.3s;
}`}
        />
        <ul
          className="dark-vertical"
          style={{
            listStyle: "none",
            padding: 0,
            width: "200px",
            background: "#111827",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  display: "block",
                  padding: "12px",
                  color: "white",
                  textDecoration: "none",
                  transition: "0.3s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#2563eb";
                  e.target.style.transform = "translateX(5px)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.transform = "translateX(0)";
                }}
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Gradient Sidebar</h2>
        <CodeCopyBlock
          code={`ul.gradient-vertical li a:hover {
  background: rgba(255,255,255,0.3);
  border-radius: 6px;
}`}
        />
        <ul
          className="gradient-vertical"
          style={{
            listStyle: "none",
            padding: 0,
            width: "200px",
            background: "linear-gradient(180deg, #3b82f6, #06b6d4)",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  display: "block",
                  padding: "12px",
                  color: "white",
                  textDecoration: "none",
                  transition: "0.3s",
                }}
                onMouseOver={(e) =>
                  (e.target.style.background = "rgba(255,255,255,0.3)")
                }
                onMouseOut={(e) => (e.target.style.background = "transparent")}
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Bordered Sidebar</h2>
        <CodeCopyBlock
          code={`ul.bordered-vertical li a:hover {
  background: #e5e7eb;
  font-weight: bold;
}`}
        />
        <ul
          className="bordered-vertical"
          style={{
            listStyle: "none",
            padding: 0,
            width: "200px",
            border: "1px solid #9ca3af",
          }}
        >
          {navItems.map((item, index) => (
            <li key={item}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  display: "block",
                  padding: "12px",
                  borderBottom:
                    index !== navItems.length - 1
                      ? "1px solid #d1d5db"
                      : "none",
                  textDecoration: "none",
                  color: "#111827",
                  transition: "0.3s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#e5e7eb";
                  e.target.style.fontWeight = "bold";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.fontWeight = "normal";
                }}
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 5 */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Rounded Buttons Sidebar</h2>
        <CodeCopyBlock
          code={`ul.rounded-vertical li a:hover {
  background: #34d399;
  transform: scale(1.05);
}`}
        />
        <ul
          className="rounded-vertical"
          style={{
            listStyle: "none",
            padding: "10px",
            width: "200px",
            background: "#f0fdf4",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  display: "block",
                  padding: "10px",
                  margin: "5px 0",
                  background: "#10b981",
                  color: "white",
                  borderRadius: "6px",
                  textDecoration: "none",
                  textAlign: "center",
                  transition: "0.3s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#34d399";
                  e.target.style.transform = "scale(1.05)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "#10b981";
                  e.target.style.transform = "scale(1)";
                }}
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 6 */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ Sidebar with Icons</h2>
        <CodeCopyBlock
          code={`ul.icon-vertical li a:hover {
  background: #dbeafe;
  color: #1d4ed8;
}`}
        />
        <ul
          className="icon-vertical"
          style={{
            listStyle: "none",
            padding: 0,
            width: "200px",
            background: "#f9fafb",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px",
                  textDecoration: "none",
                  color: "#111827",
                  transition: "0.3s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#dbeafe";
                  e.target.style.color = "#1d4ed8";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.color = "#111827";
                }}
              >
                <span style={{ marginRight: "8px" }}>🔹</span> {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 7 */}
      <div className="tutorial-section">
        <h2 className="section-title">7️⃣ Active Link Highlight</h2>
        <CodeCopyBlock
          code={`ul.active-vertical li a:hover {
  background: #2563eb;
  color: white;
}`}
        />
        <ul
          className="active-vertical"
          style={{
            listStyle: "none",
            padding: 0,
            width: "200px",
            background: "#f3f4f6",
          }}
        >
          {navItems.map((item, index) => (
            <li key={item}>
              <a
                href={`#${item.toLowerCase()}`}
                className={index === 0 ? "active" : ""}
                style={{
                  display: "block",
                  padding: "12px",
                  textDecoration: "none",
                  color: index === 0 ? "white" : "#111827",
                  background: index === 0 ? "#3b82f6" : "transparent",
                  transition: "0.3s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#2563eb";
                  e.target.style.color = "white";
                }}
                onMouseOut={(e) => {
                  if (index !== 0) {
                    e.target.style.background = "transparent";
                    e.target.style.color = "#111827";
                  }
                }}
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 8 */}
      <div className="tutorial-section">
        <h2 className="section-title">8️⃣ Minimal Outline Sidebar</h2>
        <CodeCopyBlock
          code={`ul.outline-vertical li a:hover {
  background: #eff6ff;
  box-shadow: 0 0 5px #3b82f6;
}`}
        />
        <ul
          className="outline-vertical"
          style={{ listStyle: "none", padding: 0, width: "200px" }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  border: "1px solid #3b82f6",
                  margin: "5px 0",
                  display: "block",
                  padding: "10px",
                  borderRadius: "6px",
                  color: "#3b82f6",
                  textAlign: "center",
                  textDecoration: "none",
                  transition: "0.3s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#eff6ff";
                  e.target.style.boxShadow = "0 0 5px #3b82f6";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.boxShadow = "none";
                }}
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 9 */}
      <div className="tutorial-section">
        <h2 className="section-title">9️⃣ Neon Effect Sidebar</h2>
        <CodeCopyBlock
          code={`ul.neon-vertical li a:hover {
  color: #38bdf8;
  text-shadow: 0 0 10px #38bdf8, 0 0 20px #38bdf8;
}`}
        />
        <ul
          className="neon-vertical"
          style={{
            listStyle: "none",
            padding: 0,
            width: "200px",
            background: "#0f172a",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  display: "block",
                  padding: "12px",
                  color: "#0ea5e9",
                  textDecoration: "none",
                  textShadow: "0 0 5px #0ea5e9, 0 0 10px #0ea5e9",
                  transition: "0.3s",
                }}
                onMouseOver={(e) => {
                  e.target.style.color = "#38bdf8";
                  e.target.style.textShadow =
                    "0 0 10px #38bdf8, 0 0 20px #38bdf8";
                }}
                onMouseOut={(e) => {
                  e.target.style.color = "#0ea5e9";
                  e.target.style.textShadow =
                    "0 0 5px #0ea5e9, 0 0 10px #0ea5e9";
                }}
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Example 10 */}
      <div className="tutorial-section">
        <h2 className="section-title">🔟 Card Style Sidebar</h2>
        <CodeCopyBlock
          code={`ul.card-vertical li a:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
}`}
        />
        <ul
          className="card-vertical"
          style={{
            listStyle: "none",
            padding: 0,
            width: "200px",
            background: "#f3f4f6",
          }}
        >
          {navItems.map((item) => (
            <li key={item} style={{ margin: "10px 0" }}>
              <a
                href={`#${item.toLowerCase()}`}
                style={{
                  display: "block",
                  padding: "12px",
                  background: "white",
                  borderRadius: "8px",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                  textDecoration: "none",
                  color: "#111827",
                  transition: "0.3s",
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = "scale(1.05)";
                  e.target.style.boxShadow = "0 4px 10px rgba(0,0,0,0.2)";
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 2px 5px rgba(0,0,0,0.1)";
                }}
              >
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Vertical_Navigation_Bar;
